/**
 * Presigned URL 图片上传服务
 * 
 * 核心功能：
 * 1. 获取 presigned URL
 * 2. 直接上传到 MinIO/S3
 * 3. 删除文件
 * 4. 生成结构化文件名
 * 
 * 参考来源: smark-frontend/src/services/presignedUploadService.ts
 * 移植版本: JavaScript for React (summer-art-studio)
 */

import apiClient from './apiClient';
import { compressImage, formatFileSize } from '../utils/imageCompressor';
import { convertToProxyUrl } from '../utils/imageUrl';

/**
 * Presigned URL 上传服务类
 */
export class PresignedUploadService {
  /**
   * 获取 presigned URL
   * 
   * @param {Object} request - 请求参数
   * @param {string} request.fileName - 文件名（含路径）
   * @param {string} request.contentType - 文件类型
   * @param {string} request.imageType - 图片类型
   * @param {string} request.studentId - 学生ID
   * @param {string} request.studentName - 学生姓名
   * @param {string} request.courseId - 课程ID
   * @param {string} request.courseName - 课程名称
   * @returns {Promise<Object>} { success, presignedUrl, finalUrl, message }
   */
  async getPresignedUrl(request) {
    try {
      const response = await apiClient.post('/api/upload/presigned-url', request);

      // apiClient 已自动返回 response.data
      if (response && response.success !== false) {
        return {
          success: true,
          presignedUrl: response.presignedUrl,
          finalUrl: response.finalUrl,
          bucket: response.bucket,
          key: response.key
        };
      }

      return {
        success: false,
        presignedUrl: '',
        finalUrl: '',
        message: response?.message || '获取上传链接失败'
      };
    } catch (error) {
      console.error('获取 presigned URL 失败:', error);
      return {
        success: false,
        presignedUrl: '',
        finalUrl: '',
        message: error?.response?.data?.message || error?.message || '获取上传链接失败'
      };
    }
  }

  /**
   * 上传文件到 presigned URL
   * 
   * @param {string} presignedUrl - 预签名URL
   * @param {File} file - 文件对象
   * @returns {Promise<Object>} { success, url, error }
   */
  async uploadToPresignedUrl(presignedUrl, file) {
    try {
      // 解决 Mixed Content 和内网访问问题
      let finalPresignedUrl = presignedUrl;
      
      // 自动通过 convertToProxyUrl 转换为代理相对路径 (/storage/...)，彻底解决 Mixed Content 与跨网段访问问题
      finalPresignedUrl = convertToProxyUrl(presignedUrl);
      console.log("[PresignedUpload] 预签名 URL 转换:", presignedUrl, "->", finalPresignedUrl);

      const contentType = file.type || (file.name.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg');
      
      console.log(`[PresignedUpload] 开始实际上传: ${file.name}, 类型: ${contentType}, 大小: ${file.size}`);

      const response = await fetch(finalPresignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType
        }
      });

      if (!response.ok) {
        throw new Error(`上传失败: HTTP ${response.status}`);
      }

      return {
        success: true,
        url: this.extractFinalUrl(presignedUrl)
      };
    } catch (error) {
      console.error('[PresignedUpload] fetch 异常:', error);
      let errorMsg = error.message || '网络连接失败';
      if (errorMsg === 'Load failed') {
        errorMsg = '网络加载失败 (Load failed)，请检查网络或重试';
      }
      return {
        success: false,
        error: errorMsg
      };
    }
  }

  /**
   * 完整的文件上传流程
   * 
   * @param {File} file - 文件对象
   * @param {string} imageType - 图片类型 (课堂记录/购买记录/上课记录/作品)
   * @param {string} studentId - 学生ID
   * @param {string} studentName - 学生姓名
   * @param {string} courseId - 课程ID
   * @param {string} courseName - 课程名称
   * @param {string} teacherName - 老师姓名
   * @returns {Promise<Object>} { success, url, error }
   */
  async uploadFile(file, imageType, studentId, studentName, courseId, courseName, teacherName) {
    try {
      // 0. 压缩图片（如果需要）
      let fileToUpload = file;
      const originalSize = file.size;

      // 仅对图片文件进行压缩
      if (file.type.startsWith('image/')) {
        console.log(`[PresignedUpload] 原始文件大小: ${formatFileSize(originalSize)}`);

        fileToUpload = await compressImage(file, {
          maxSizeMB: 0.5,         // 压缩到 500KB 以下
          maxWidthOrHeight: 1280, // 最大边长 1280px
          initialQuality: 0.9    // 初始质量 90%
        });

        if (fileToUpload.size !== originalSize) {
          console.log(`[PresignedUpload] 压缩后大小: ${formatFileSize(fileToUpload.size)} (节省 ${((1 - fileToUpload.size / originalSize) * 100).toFixed(1)}%)`);
        }
      }

      // 1. 生成文件名
      const fileName = this.generateFileName(
        fileToUpload,
        imageType,
        studentId,
        studentName,
        courseId,
        courseName,
        teacherName
      );

      console.log('上传文件:', {
        fileName,
        originalSize: formatFileSize(originalSize),
        compressedSize: formatFileSize(fileToUpload.size),
        fileType: fileToUpload.type,
        imageType,
        studentId,
        courseName
      });

      // 2. 获取 presigned URL
      const presignedResponse = await this.getPresignedUrl({
        fileName,
        contentType: fileToUpload.type,
        imageType,
        studentId,
        studentName,
        courseId,
        courseName
      });

      if (!presignedResponse.success) {
        return {
          success: false,
          error: presignedResponse.message || '获取上传链接失败'
        };
      }

      // 3. 上传文件到 presigned URL
      const uploadResult = await this.uploadToPresignedUrl(
        presignedResponse.presignedUrl,
        fileToUpload
      );

      if (uploadResult.success) {
        console.log('[PresignedUpload] 文件上传成功:', {
          finalUrl: presignedResponse.finalUrl,
          extractedUrl: uploadResult.url
        });
        return {
          success: true,
          url: presignedResponse.finalUrl || uploadResult.url
        };
      } else {
        console.error('[PresignedUpload] 物理上传失败:', uploadResult.error);
        return uploadResult;
      }
    } catch (error) {
      console.error('[PresignedUpload] 流程异常:', error);
      return {
        success: false,
        error: error.message || '上传异常'
      };
    }
  }

  /**
   * 批量上传文件
   * 
   * @param {File[]} files - 文件数组
   * @param {string} imageType - 图片类型
   * @param {string} studentId - 学生ID
   * @param {string} studentName - 学生姓名
   * @param {string} courseId - 课程ID
   * @param {string} courseName - 课程名称
   * @param {string} teacherName - 老师姓名
   * @param {Function} onProgress - 进度回调 (current, total)
   * @returns {Promise<Object[]>} 上传结果数组
   */
  async batchUpload(
    files,
    imageType,
    studentId,
    studentName,
    courseId,
    courseName,
    teacherName,
    onProgress
  ) {
    const results = [];
    const total = files.length;

    for (let i = 0; i < files.length; i++) {
      if (onProgress) {
        onProgress(i, total);
      }

      const result = await this.uploadFile(
        files[i],
        imageType,
        studentId,
        studentName,
        courseId,
        courseName,
        teacherName
      );

      results.push(result);
    }

    if (onProgress) {
      onProgress(total, total);
    }

    return results;
  }

  /**
   * 生成文件名（包含文件夹路径）
   * 
   * 格式: {folderPath}/{fileName}_{date}_{timestamp}.{ext}
   * 
   * 文件夹结构:
   * - 课堂记录/上课记录/作品: {studentName}_{studentId}/
   * - 购买记录: 购买凭证/
   * 
   * 文件名格式:
   * {courseName}_{courseId}_{teacherName}_{studentName}_{studentId}_{type}_{date}_{timestamp}.{ext}
   * 
   * @param {File} file - 文件对象
   * @param {string} imageType - 图片类型
   * @param {string} studentId - 学生ID
   * @param {string} studentName - 学生姓名
   * @param {string} courseId - 课程ID
   * @param {string} courseName - 课程名称
   * @param {string} teacherName - 老师姓名
   * @returns {string} 完整文件路径
   */
  generateFileName(file, imageType, studentId, studentName, courseId, courseName, teacherName) {
    // 获取北京时间 - 增加错误处理以支持不兼容的浏览器
    let beijingDate;
    try {
      beijingDate = new Date(
        new Date().toLocaleString('en-US', { timeZone: 'Asia/Shanghai' })
      );
    } catch (e) {
      console.warn('[PresignedUpload] 浏览器不支持时区配置，使用本地时间');
      beijingDate = new Date();
    }

    const dateStr = beijingDate.toISOString().split('T')[0]; // 2025-12-23
    const timeStr = this.generateTimestamp();
    const ext = file.name.split('.').pop() || 'jpg';

    let folderPath = '';
    let fileName = '';

    // 清理名称中的特殊字符
    const cleanName = (str) => (str || '').replace(/[|｜]/g, '');
    const cleanCourseName = courseName ? cleanName(courseName) : '';
    const cleanStudentName = studentName ? cleanName(studentName) : '';
    const cleanTeacherName = teacherName ? cleanName(teacherName) : '';

    switch (imageType) {
      case '课堂记录':
      case '课堂': {
        // 文件夹: 学生姓名_学生ID
        const student = cleanStudentName || 'unknown';
        folderPath = `${student}_${studentId || 'unknown'}`;

        // 文件名: 课程_课程ID_老师_学生_学生ID_课堂记录
        const course = cleanCourseName || '未知课程';
        const teacher = cleanTeacherName || '未知老师';
        fileName = `${course}_${courseId || 'unknown'}_${teacher}_${student}_${studentId || 'unknown'}`;
        fileName += imageType === '课堂' ? '_课堂' : '_课堂记录';
        break;
      }

      case '购买记录': {
        // 文件夹: 购买凭证
        folderPath = '购买凭证';

        // 文件名: 课程_课程ID_老师_学生_学生ID_购买记录
        const course = cleanCourseName || '未知课程';
        const student = cleanStudentName || '未知学生';
        const teacher = cleanTeacherName || '未知老师';
        fileName = `${course}_${courseId || 'unknown'}_${teacher}_${student}_${studentId || 'unknown'}_购买记录`;
        break;
      }

      case '上课记录': {
        // 文件夹: 学生姓名_学生ID
        const student = cleanStudentName || 'unknown';
        folderPath = `${student}_${studentId || 'unknown'}`;

        // 文件名: 课程_课程ID_老师_学生_学生ID_上课记录
        const course = cleanCourseName || '未知课程';
        const teacher = cleanTeacherName || '未知老师';
        fileName = `${course}_${courseId || 'unknown'}_${teacher}_${student}_${studentId || 'unknown'}_上课记录`;
        break;
      }

      case '作品': {
        // 文件夹: 学生姓名_学生ID
        const student = cleanStudentName || 'unknown';
        folderPath = `${student}_${studentId || 'unknown'}`;

        // 文件名: 课程_课程ID_老师_学生_学生ID_作品
        const course = cleanCourseName || '未知课程';
        const teacher = cleanTeacherName || '未知老师';
        fileName = `${course}_${courseId || 'unknown'}_${teacher}_${student}_${studentId || 'unknown'}_作品`;
        break;
      }

      default: {
        // 默认：学生姓名_学生ID
        const student = cleanStudentName || 'unknown';
        folderPath = `${student}_${studentId || 'unknown'}`;
        fileName = `${student}_${studentId || 'unknown'}`;
        break;
      }
    }

    // 完整文件名: fileName_date_timestamp.ext
    const fullFileName = `${fileName}_${dateStr}_${timeStr}.${ext}`;

    // 返回完整路径: folderPath/fullFileName
    return folderPath ? `${folderPath}/${fullFileName}` : fullFileName;
  }

  /**
   * 生成时间戳（Base36编码的时间戳 + 随机数）
   * 
   * @returns {string} 时间戳字符串
   */
  generateTimestamp() {
    let beijingDate;
    try {
      beijingDate = new Date(
        new Date().toLocaleString('en-US', { timeZone: 'Asia/Shanghai' })
      );
    } catch (e) {
      beijingDate = new Date();
    }
    const timestamp = beijingDate.getTime();
    const random = Math.floor(Math.random() * 10000);
    return timestamp.toString(36) + random.toString(36);
  }

  /**
   * 从 presigned URL 提取最终的文件 URL
   * 
   * @param {string} presignedUrl - 预签名URL
   * @returns {string} 最终URL（去除查询参数）
   */
  extractFinalUrl(presignedUrl) {
    try {
      const url = new URL(presignedUrl);
      return `${url.protocol}//${url.host}${url.pathname}`;
    } catch {
      return presignedUrl;
    }
  }

  /**
   * 删除文件
   * 
   * @param {string} imageUrl - 图片URL
   * @returns {Promise<boolean>} 是否成功
   */
  async deleteFile(imageUrl) {
    try {
      const fileKey = this.extractKeyFromUrl(imageUrl);
      console.log('删除文件:', { imageUrl, fileKey });

      const response = await apiClient.post('/api/upload/delete', { key: fileKey });

      return response.success || false;
    } catch (error) {
      console.error('删除文件失败:', error);
      return false;
    }
  }

  /**
   * 从URL中提取文件key
   * 
   * @param {string} imageUrl - 图片URL
   * @returns {string} 文件key
   */
  extractKeyFromUrl(imageUrl) {
    try {
      const url = new URL(imageUrl);
      let pathname = url.pathname;

      // 移除开头的斜杠
      if (pathname.startsWith('/')) {
        pathname = pathname.substring(1);
      }

      // URL解码
      pathname = decodeURIComponent(pathname);

      // 移除bucket名称前缀
      const bucketName = import.meta.env.VITE_S3_BUCKET || 'piclist-sumart';
      if (pathname.startsWith(bucketName + '/')) {
        pathname = pathname.substring(bucketName.length + 1);
      }

      return pathname;
    } catch (error) {
      console.error('提取文件key失败:', error);
      return imageUrl;
    }
  }

  /**
   * 检查文件是否存在
   * 
   * @param {string} imageUrl - 图片URL
   * @returns {Promise<boolean>} 是否存在
   */
  async checkFileExists(imageUrl) {
    try {
      const fileKey = this.extractKeyFromUrl(imageUrl);
      const response = await apiClient.get('/api/upload/check-exists', {
        params: { key: fileKey }
      });

      return response.exists || false;
    } catch (error) {
      console.error('检查文件存在失败:', error);
      return false;
    }
  }
}

// 导出单例实例
export const presignedUploadService = new PresignedUploadService();
export default presignedUploadService;
